import { useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import { useOrgCalendar, useSetUserDates } from "@/api/queries";
import { canEditOrgDates, useSession } from "@/api/session";
import type { OrgCalendarPerson } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { CategoryBarChart, StatTile } from "@/components/charts";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable } from "@/components/ui/states";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { daysUntil, nextAnniversary, toDateStr, todayStr, yearsSince } from "@/lib/calendar";
import { formatDate } from "@/lib/format";
import { UpcomingWfhPanel } from "@/pages/shared/wfh-upcoming";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface CalendarEvent {
  id: string;
  personId: number;
  name: string;
  designation: string;
  kind: "birthday" | "anniversary";
  date: Date;
  daysAway: number;
  years: number | null;
}

/**
 * Visible to every role. Built entirely from real dates people entered during
 * their own onboarding, so an account that has not filled them in simply does
 * not appear rather than being shown with a placeholder date.
 */
export function OrgCalendarPage() {
  const { data, isLoading, error, refetch } = useOrgCalendar();
  const { user } = useSession();
  const canEdit = canEditOrgDates(user);
  const [editingPerson, setEditingPerson] = useState<OrgCalendarPerson | null>(null);

  const events = useMemo<CalendarEvent[]>(() => {
    const list: CalendarEvent[] = [];
    for (const person of data?.people ?? []) {
      if (person.date_of_birth) {
        const date = nextAnniversary(person.date_of_birth);
        list.push({
          id: `b-${person.id}`,
          personId: person.id,
          name: person.name ?? "Unnamed",
          designation: person.designation,
          kind: "birthday",
          date,
          daysAway: daysUntil(date),
          years: null,
        });
      }
      if (person.date_joined) {
        const date = nextAnniversary(person.date_joined);
        const years = yearsSince(person.date_joined, date);
        // A joining date only becomes a work anniversary after the first year.
        if (years >= 1) {
          list.push({
            id: `a-${person.id}`,
            personId: person.id,
            name: person.name ?? "Unnamed",
            designation: person.designation,
            kind: "anniversary",
            date,
            daysAway: daysUntil(date),
            years,
          });
        }
      }
    }
    return list.sort((a, b) => a.daysAway - b.daysAway);
  }, [data?.people]);

  const upcoming = events.filter((e) => e.daysAway <= 30);
  const fixedHolidays = (data?.holidays ?? []).filter((h) => h.category !== "optional");
  const optionalHolidays = (data?.holidays ?? []).filter((h) => h.category === "optional");

  const upcomingHolidays = useMemo(() => {
    const today = todayStr();
    return fixedHolidays.filter((h) => h.date >= today).slice(0, 6);
  }, [fixedHolidays]);

  // Distribution of celebrations across the year, from real dates only.
  const byMonth = useMemo(() => {
    const counts = new Array(12).fill(0) as number[];
    for (const event of events) counts[event.date.getMonth()] = (counts[event.date.getMonth()] ?? 0) + 1;
    return MONTHS.map((label, index) => ({ label, value: counts[index] ?? 0 }));
  }, [events]);

  const missingDates = (data?.people ?? []).filter((p) => !p.date_of_birth || !p.date_joined).length;

  if (error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Visible to everyone" title="Org Calendar" />
        <Panel>
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Visible to everyone"
        title="Org Calendar"
        sub="Birthdays, work anniversaries and the company holiday list, across all of Mentoria."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Celebrations in the next 30 days"
          value={upcoming.length}
          icon="birthday"
          loading={isLoading}
        />
        <StatTile
          label="People on the calendar"
          value={data?.people.length ?? 0}
          icon="team"
          loading={isLoading}
          {...(missingDates > 0
            ? { meta: `${missingDates} still to add their dates` }
            : {})}
        />
        <StatTile
          label="Paid company holidays"
          value={fixedHolidays.length}
          icon="holiday"
          loading={isLoading}
          meta="Fixed, org-wide"
        />
        <StatTile
          label="Optional holidays"
          value={optionalHolidays.length}
          icon="calendar"
          loading={isLoading}
          meta="Apply for up to 2 a year"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel className="min-w-0">
          <PanelHeader
            title="Upcoming"
            note="Next 30 days"
            actions={
              <span className="text-xs text-muted">
                {upcoming.length} {upcoming.length === 1 ? "event" : "events"}
              </span>
            }
          />
          {isLoading ? (
            <SkeletonTable rows={5} cols={3} />
          ) : upcoming.length === 0 ? (
            <EmptyState
              icon="calendar"
              title="Nothing in the next 30 days"
              body={
                missingDates > 0
                  ? "Some accounts have not entered their date of birth or joining date yet, so they will not appear here until they do."
                  : "The next celebration is further out. Check the full year list below."
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {upcoming.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="min-w-0">
          <PanelHeader title="Next company holidays" note="Paid, org-wide closures" />
          {isLoading ? (
            <SkeletonTable rows={4} cols={2} />
          ) : upcomingHolidays.length === 0 ? (
            <EmptyState icon="holiday" title="No upcoming holidays on file" />
          ) : (
            <ul className="divide-y divide-line">
              {upcomingHolidays.map((holiday) => (
                <li key={holiday.date} className="flex items-center gap-3 px-4 py-3">
                  <Icon name="holiday" size={20} className="shrink-0 text-faint" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{holiday.name}</p>
                    <p className="font-mono text-xs text-muted">{formatDate(holiday.date)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Who will not be in the office, next to who is celebrating and when the
          company is closed — all three are the same question about the days
          ahead, so they belong on the same screen. Scoped by role inside the
          component: an employee sees only their own. */}
      <UpcomingWfhPanel />

      <CategoryBarChart
        title="Celebrations across the year"
        note="Birthdays and work anniversaries by month, from the dates people have entered"
        data={byMonth}
        valueHeader="Events"
        orientation="vertical"
        loading={isLoading}
        emptyBody="No dates have been entered yet."
      />

      <Panel>
        <PanelHeader title="Full year" note="Everything on the calendar, soonest first" />
        <Tabs defaultValue="people">
          <div className="border-b border-line px-4 py-2.5">
            <TabsList>
              <TabsTrigger value="people">People</TabsTrigger>
              <TabsTrigger value="fixed">Company holidays</TabsTrigger>
              <TabsTrigger value="optional">Optional holidays</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="people">
            {isLoading ? (
              <SkeletonTable rows={6} cols={3} />
            ) : events.length === 0 ? (
              <EmptyState
                icon="calendar"
                title="No dates on file yet"
                body="Birthdays and joining dates are entered by each person during their own account setup."
              />
            ) : (
              <ul className="divide-y divide-line">
                {events.map((event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    onEdit={
                      canEdit
                        ? () => {
                            const person = (data?.people ?? []).find((p) => p.id === event.personId);
                            if (person) setEditingPerson(person);
                          }
                        : undefined
                    }
                  />
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="fixed">
            <HolidayList holidays={fixedHolidays} loading={isLoading} />
          </TabsContent>

          <TabsContent value="optional">
            <HolidayList holidays={optionalHolidays} loading={isLoading} />
          </TabsContent>
        </Tabs>
      </Panel>

      {editingPerson && (
        <EditDatesDialog person={editingPerson} onClose={() => setEditingPerson(null)} />
      )}
    </div>
  );
}

function EventRow({ event, onEdit }: { event: CalendarEvent; onEdit?: () => void }) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Avatar name={event.name} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{event.name}</p>
        <p className="truncate text-xs text-muted">{event.designation}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <Badge
          tone={event.kind === "birthday" ? "accent" : "good"}
          icon={event.kind === "birthday" ? "birthday" : "anniversary"}
        >
          {event.kind === "birthday" ? "Birthday" : `${event.years} ${event.years === 1 ? "year" : "years"}`}
        </Badge>
        <div className="w-24 text-right">
          <p className="font-mono text-xs tabular-nums text-ink-soft">
            {formatDate(toDateStr(event.date), "d MMM")}
          </p>
          <p className="text-xs text-muted">
            {event.daysAway === 0 ? "Today" : event.daysAway === 1 ? "Tomorrow" : `in ${event.daysAway} days`}
          </p>
        </div>
        {onEdit && (
          <Button
            variant="ghost"
            size="icon"
            iconLeft="edit"
            aria-label={`Edit ${event.name}'s dates`}
            onClick={onEdit}
          />
        )}
      </div>
    </li>
  );
}

/** Vyomi-only: correct a person's birthday and/or joining date. Both fields are
 *  required together, matching the backend (PUT /api/admin/users/:id/dates). */
function EditDatesDialog({ person, onClose }: { person: OrgCalendarPerson; onClose: () => void }) {
  const setDates = useSetUserDates();
  const [dateOfBirth, setDateOfBirth] = useState(person.date_of_birth ?? "");
  const [dateJoined, setDateJoined] = useState(person.date_joined ?? "");
  const [error, setError] = useState<string | null>(null);

  async function onSave() {
    setError(null);
    if (!dateOfBirth || !dateJoined) {
      setError("Both dates are required.");
      return;
    }
    try {
      await setDates.mutateAsync({ id: person.id, dateOfBirth, dateJoined });
      toast.success(`${person.name ?? "Their"} dates were updated.`);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save those dates.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={`Edit dates — ${person.name ?? "Unnamed"}`}
          description={person.designation}
        />
        <DialogBody className="flex flex-col gap-4">
          <Field label="Birthday" htmlFor="edit-dob">
            <Input
              id="edit-dob"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
          </Field>
          <Field label="Work anniversary" htmlFor="edit-joined" hint="The date they joined Mentoria.">
            <Input
              id="edit-joined"
              type="date"
              value={dateJoined}
              onChange={(e) => setDateJoined(e.target.value)}
            />
          </Field>
          {error && (
            <p role="alert" className="flex items-start gap-1.5 text-xs text-bad">
              <Icon name="warning-circle" size={16} />
              <span>{error}</span>
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={setDates.isPending}>
            Cancel
          </Button>
          <Button variant="primary" iconLeft="save" onClick={onSave} loading={setDates.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HolidayList({
  holidays,
  loading,
}: {
  holidays: { date: string; name: string }[];
  loading: boolean;
}) {
  if (loading) return <SkeletonTable rows={6} cols={2} />;
  if (holidays.length === 0) return <EmptyState icon="holiday" title="No holidays on file" />;
  return (
    <ul className="divide-y divide-line">
      {holidays.map((holiday) => (
        <li key={holiday.date} className="flex items-center gap-3 px-4 py-2.5">
          <span className="w-24 shrink-0 font-mono text-xs tabular-nums text-muted">
            {formatDate(holiday.date, "d MMM yyyy")}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-ink">{holiday.name}</span>
        </li>
      ))}
    </ul>
  );
}
